import { getNormalizedError } from '@/shared/api/lib';
import { FocusSummaryCard } from '@/shared/components';
import { formatCurrency } from '@/shared/lib/currency';
import { useFacilityScope } from '@/shared/lib/hooks/use-facility-scope';
import { shouldShowSparkline } from '@/shared/lib/sparkline-range';
import { useQuery } from '@tanstack/react-query';
import type { Dayjs } from 'dayjs';
import { type FC } from 'react';
import { getExpensesQueryOptions } from '../api/get-expenses.query';

interface ExpensesSummaryCardProps {
  dateRange: {
    from: Dayjs;
    to: Dayjs;
  };
}

export const ExpensesSummaryCard: FC<ExpensesSummaryCardProps> = ({
  dateRange,
}) => {
  const { scopedFacilityId } = useFacilityScope();
  const { data, isLoading, error } = useQuery(
    getExpensesQueryOptions({
      from: dateRange.from.format('YYYY-MM-DD'),
      to: dateRange.to.format('YYYY-MM-DD'),
      facilitiesIds: scopedFacilityId ? [scopedFacilityId] : undefined,
    })
  );

  const showSparkline = shouldShowSparkline(dateRange);

  const isForbidden = getNormalizedError(error).statusCode === 422;

  if (isForbidden) {
    return (
      <FocusSummaryCard
        valueAsComment
        icon="refresh"
        title="Expenses"
        tooltipMessage="Total payroll expenses for staff"
        value="Please enter staff salaries on the platform to see this metric"
        loading={false}
      />
    );
  }

  if (!isLoading && !data) {
    return (
      <FocusSummaryCard
        value="N/A"
        icon="refresh"
        title="Expenses"
        loading={false}
      />
    );
  }

  if (data?.expenses?.insufficientData) {
    return (
      <FocusSummaryCard
        valueAsComment
        icon="refresh"
        title="Expenses"
        tooltipMessage="Total payroll expenses for staff"
        value={data.expenses.message ?? 'Not enough data to display expenses.'}
        loading={false}
      />
    );
  }

  const amount = data?.expenses?.amount;
  const changePct = data?.expenses?.changePct;
  const sparklinePoints = data?.sparkline?.points || [];

  const chartData = {
    labels: sparklinePoints.map((_, index) => index.toString()),
    datasets: [
      {
        data: sparklinePoints.map((point) => point.value),
        borderColor: '#165BAA',
        backgroundColor: 'transparent',
        fill: false,
      },
    ],
  };

  const indicator =
    changePct != null
      ? {
          value: changePct,
          negativeValue: changePct < 0,
        }
      : undefined;

  return (
    <FocusSummaryCard
      icon="refresh"
      title="Expenses"
      tooltipMessage="Total payroll expenses for staff"
      indicator={indicator}
      chart={showSparkline ? { data: chartData } : undefined}
      value={amount != null ? formatCurrency(amount) : 'N/A'}
      loading={isLoading}
    />
  );
};
